import { ApiError, apiErrorFromResponse } from "./errors";

export type FetchImpl = typeof fetch;

export interface HttpClientOptions {
  /** Base de l'API, ex. `http://localhost:8080`. Le chemin `/api/v1` est ajouté ici. */
  baseUrl: string;
  /** Injection de `fetch` — la couche de mocks fournit le sien. */
  fetchImpl?: FetchImpl;
  /** Jeton de session (poste/rôle). Résolu paresseusement à chaque requête. */
  getAuthToken?: () => string | null | undefined;
  /**
   * Notifié dès qu'une route répond `401` — SAUF les endpoints
   * d'authentification eux-mêmes (`AUTH_PATHS`), qui gèrent leur propre `401`.
   * Câblé par `createBackend()` sur `emitUnauthorized()` : la porte d'accès
   * applicative (contrat v1.38 §8bis) rebascule alors sur l'écran de connexion.
   */
  onUnauthorized?: () => void;
  /** Délai avant abandon d'une requête, en ms. */
  timeoutMs?: number;
}

export type QueryValue = string | number | boolean | null | undefined | ReadonlyArray<string | number>;

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /** Objet JSON, ou `FormData` pour un envoi `multipart/form-data` (upload). */
  body?: unknown;
  /**
   * Dépassement du délai par défaut du client (v1.56, assistant IA — un tour
   * peut enchaîner plusieurs outils coûteux, OSRM notamment, et dépasser le
   * délai standard des routes consultatives).
   */
  timeoutMs?: number;
}

const API_PREFIX = "/api/v1";

/**
 * Endpoints d'authentification : un `401` y est une réponse **métier** (mauvais
 * identifiants), pas une session perdue — on ne déclenche donc pas
 * `onUnauthorized` pour eux. Le reste de l'API, sous la porte d'accès
 * applicative (contrat v1.38 §8bis), n'a jamais de `401` légitime une fois
 * connecté : un `401` y signifie « cookie expiré / révoqué ».
 */
const AUTH_PATHS: ReadonlySet<string> = new Set([
  "/auth/session",
  "/auth/login",
  "/auth/logout",
  "/auth/me",
]);

/**
 * Rend la base absolue. Une base vide ou relative (API servie sur la même
 * origine que le client, cas courant en production) est résolue contre
 * `location.origin` ; hors navigateur (tests, SSR), on retombe sur une origine
 * synthétique — inoffensive, la couche de mocks ignore l'hôte.
 */
function resolveBaseUrl(raw: string): string {
  const trimmed = raw.replace(/\/+$/, "");
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const origin = globalThis.location?.origin;
  if (origin && origin !== "null") return `${origin}${trimmed}`;
  return `http://sentinelle.local${trimmed}`;
}

function buildQuery(query: Record<string, QueryValue> | undefined): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      if (value.length > 0) params.set(key, value.join(","));
    } else {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** Client HTTP bas niveau : préfixe, jeton, timeout, erreurs normalisées. */
export class HttpClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: FetchImpl;
  private readonly getAuthToken: (() => string | null | undefined) | undefined;
  private readonly onUnauthorized: (() => void) | undefined;
  private readonly timeoutMs: number;

  constructor(options: HttpClientOptions) {
    this.baseUrl = resolveBaseUrl(options.baseUrl);
    // Lié à globalThis : un fetch détaché perd son receveur dans certains moteurs.
    this.fetchImpl = options.fetchImpl ?? ((...args) => globalThis.fetch(...args));
    this.getAuthToken = options.getAuthToken;
    this.onUnauthorized = options.onUnauthorized;
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  /** Construit l'URL ABSOLUE d'un chemin d'API (`path` commence par `/`). */
  url(path: string, query?: Record<string, QueryValue>): string {
    return `${this.baseUrl}${API_PREFIX}${path}${buildQuery(query)}`;
  }

  async get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>("GET", path, options);
  }

  async post<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>("POST", path, options);
  }

  async patch<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>("PATCH", path, options);
  }

  async put<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>("PUT", path, options);
  }

  async del<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>("DELETE", path, options);
  }

  private async request<T>(method: string, path: string, options: RequestOptions): Promise<T> {
    const url = this.url(path, options.query);
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getAuthToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;

    // `credentials: "include"` sur TOUS les appels API (brief §5) : le cookie de
    // session `sentinelle_session` (porte d'accès applicative, contrat v1.38 §8bis)
    // doit accompagner chaque requête, y compris cross-origin en développement.
    const init: RequestInit = { method, headers, credentials: "include" };
    if (options.body !== undefined) {
      if (options.body instanceof FormData) {
        // Pas de `Content-Type` manuel : le navigateur pose lui-même le
        // `multipart/form-data; boundary=…`.
        init.body = options.body;
      } else {
        headers["Content-Type"] = "application/json";
        init.body = JSON.stringify(options.body);
      }
    }

    // Compose le timeout interne avec un éventuel signal d'appelant.
    const timeout = new AbortController();
    const timer = setTimeout(
      () => timeout.abort(new DOMException("timeout", "TimeoutError")),
      options.timeoutMs ?? this.timeoutMs,
    );
    init.signal = options.signal ? anySignal([options.signal, timeout.signal]) : timeout.signal;

    let response: Response;
    try {
      response = await this.fetchImpl(url, init);
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError" && options.signal?.aborted) {
        throw cause; // abandon voulu par l'appelant : on le laisse remonter tel quel
      }
      throw new ApiError("transport_error", "La requête n'a pas abouti (réseau indisponible).", { cause });
    } finally {
      clearTimeout(timer);
    }

    if (!response.ok) {
      // `401` hors endpoints d'auth ⇒ session perdue : on prévient la porte
      // d'accès applicative avant de propager l'erreur à l'appelant.
      if (response.status === 401 && !AUTH_PATHS.has(path)) this.onUnauthorized?.();
      throw await apiErrorFromResponse(response);
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }
}

/** Équivalent minimal de `AbortSignal.any`, pour les moteurs qui ne l'ont pas. */
function anySignal(signals: AbortSignal[]): AbortSignal {
  if (typeof AbortSignal.any === "function") return AbortSignal.any(signals);
  const controller = new AbortController();
  const onAbort = (event: Event) => {
    controller.abort((event.target as AbortSignal).reason);
    for (const s of signals) s.removeEventListener("abort", onAbort);
  };
  for (const s of signals) {
    if (s.aborted) {
      controller.abort(s.reason);
      break;
    }
    s.addEventListener("abort", onAbort);
  }
  return controller.signal;
}
